<script setup lang="ts">
import { useActivityStore } from "@/utils/ActivityStore";
import { formatPrice, GSTFromTotal, Round2Num } from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb, type UserRecordModel } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { VNodeRenderer } from "@layouts/components/VNodeRenderer";
import { themeConfig } from "@themeConfig";
import { RecordModel } from "pocketbase";
import { bookingGlobalPopupStore } from "@/components/booking/bookingGlobalPopupStore";
import {storeToRefs} from "pinia";
import { useRouter } from "vue-router";

const router = useRouter()
const { openPopup } = bookingGlobalPopupStore()
const { bookingLoading } = storeToRefs(bookingGlobalPopupStore())
// Interface definitions
interface LocationDetails {
  id?: string;
  address: string | undefined;
  suburb: string | undefined;
  city: string | undefined;
  postcode: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  taxName: string | undefined;
  taxRate: number | undefined;
  taxID: string | undefined;
}

interface DocketDetails {
  id?: string;
  docketNumber: number | string | undefined;
  created: string | undefined;
  due?: number | undefined;
  user?: string | undefined;
  discountCode?: string | undefined;
  discountSources?: any[];
  discountBookings?: number;
  discountEvents?: number;
  discountProducts?: number;
  expand?: {
    user: UserRecordModel;
  };
}

interface DocketLineDetails {
  id?: string;
  qty: number;
  itemPrice: number | undefined;
  discount: number | undefined;
  lineTotal: number | undefined;
  booking?: string | undefined;
  giftCard?: string[] | undefined;
  eventTicket?: string[];
  coupon?: string | undefined;
  transactionType?: string;
  couponQty: number;
  userAccountCredit?: string | undefined;
  expand: {
    product: {
      name?: string | undefined;
    };
    booking: {
      activity?: string | undefined;
    };
    giftCard: {
      message?: string | undefined;
    };
    coupon: {
      name?: string | undefined;
    };
    userAccountCredit: UserRecordModel;
  };
}

interface DocketPaymentDetails {
  id?: number | string;
  paymentType: string | undefined;
  status: string | undefined;
  amount: number | undefined;
  surchargeAmount: number | undefined;
  tipAmount: number | undefined;
  user?: string | undefined;
  created?: string;
  source?: string;
  cardFailureCode?: string;
  expand: {
    user: UserRecordModel;
  };
}

// Stores
const globalStore = useGlobalSetupStore();
const activityStore = useActivityStore();

const { getActivitiesMap } = activityStore;

const { globalSetup } = storeToRefs(globalStore);
const { activitiesMap } = storeToRefs(activityStore);

// Route
const route = useRoute();
interface Props {
  docketId?: string;
}

const props = defineProps<Props>();


// Reactive Data
const docketSubtotal = ref<number | undefined>(0);
const docketTotalDiscount = ref<number | undefined>(0);
const docketTotal = ref<number | undefined>(0);
const creditCardSurcharge = ref<number | undefined>(0);
const creditCardTip = ref<number | undefined>(0);
const paymentsTotal = ref<number | undefined>(0);
const amountDue = ref<number | undefined>(0);
const totalPaid = ref<number | undefined>(0);
const docketLines = ref<DocketLineDetails[] | RecordModel[]>([]);
const docketPayments = ref<DocketPaymentDetails[] | RecordModel[]>([]);
const docketLocationDetails = ref<LocationDetails>({
  address: undefined,
  suburb: undefined,
  city: undefined,
  postcode: undefined,
  email: undefined,
  phone: undefined,
  taxName: undefined,
  taxRate: undefined,
  taxID: undefined,
});
const isReceiptDialogOpen = ref<boolean>(false);
const openedDocketId = ref<string>("");

const docketDetails = ref<DocketDetails>({
  docketNumber: undefined,
  created: undefined,
  due: undefined,
});

// Computed
const getUserDetails = computed(() => {
  const {
    firstName,
    lastName,
    address,
    postcode,
    suburb,
    city,
    state,
    country,
  } =
  docketDetails.value?.expand?.user ||
  docketLines.value[0]?.expand?.userAccountCredit ||
  docketPayments.value[0]?.expand?.user ||
  {};

  return {
    firstName: firstName ?? "",
    lastName: lastName ?? "",
    address: address ?? "",
    postcode: postcode ?? "",
    suburb: suburb ?? "",
    city: city ?? "",
    state: state ?? "",
    country: country ?? "",
  };
});

// Computed
const getHeaderLogo = computed(() => {
  return `${pb.baseUrl}/api/files/${globalSetup.value.collectionId}/${globalSetup.value.id}/${globalSetup.value.logo}`;
});

const getDiscountSource = computed(() => {
  let content = "";
  const membershipName =
    docketDetails?.value?.expand?.user?.expand?.membership?.name;
  const discountCode = docketDetails?.value?.discountCode;
  const discountSources = docketDetails?.value?.discountSources;
  const { discountEvents, discountProducts, discountBookings } =
    docketDetails?.value;

  // Check if there are applicable discounts
  if (
    discountSources &&
    (discountEvents || discountProducts || discountBookings)
  ) {
    const sources = [];

    if (discountSources.includes("POS")) {
      sources.push("POS");
    }
    if (discountSources.includes("DiscountCode") && discountCode) {
      sources.push(`Discount code ${discountCode}`);
    }
    if (discountSources.includes("Membership") && membershipName) {
      sources.push(membershipName);
    }

    // Join all sources with commas
    content = sources.join(", ");
  }

  return content ? `${content} Discount Applied.` : "";
});

const hasCardFailures = computed(() => {
  return docketPayments.value.some((payment) => payment.cardFailureCode);
});

// Methods
// Fetch the docket details with the docket id
const docketStatus = ref('')
const getDocketDetails = async () => {
  try {
    const record = await pb.collection("dockets").getOne(props?.docketId, {
      expand: "location,user.membership",
    });

    let allowView = false
    if(pb.authStore.model?.roles && pb.authStore.model?.roles.length  > 0){
      allowView = true
    } else if(pb.authStore?.model?.id && record.user && (record.user == pb.authStore.model.id || record.createdBy == pb.authStore.model.id)){
      allowView = true
    }

    if(!allowView){
      await router.push("/")
      return
    }
    // Destructure docket details
    const {
      docketNumber,
      created,
      discountCode,
      discountSources,
      expand,
      discountBookings,
      discountEvents,
      discountProducts,
    } = record;

    docketStatus.value = record.status

    // Assign docket details
    docketDetails.value = {
      docketNumber,
      created: moment(created).format("lll"),
      discountCode,
      discountSources,
      discountBookings,
      discountEvents,
      discountProducts,
      expand,
    };

    // Helper function to assign location details
    const assignLocationDetails = (location: LocationDetails) => {
      if (location) {
        const {
          address,
          suburb,
          city,
          postcode,
          email,
          phone,
          taxName,
          taxRate,
          taxID,
        } = location;

        docketLocationDetails.value = {
          address: address,
          suburb: suburb,
          city: city,
          postcode,
          email,
          phone,
          taxName,
          taxRate,
          taxID,
        };
      }
    };

    // Assign location details if available
    assignLocationDetails(expand?.location);

    // Fetch related details
    await getDocketPayments();
    await getDocketLines();
  } catch (error) {
    console.error("Error fetching docket details:", error);
  }
};

function formatPaymentDate(
  paymentDate: string | undefined,
  docketDate: string | undefined,
) {
  if (!paymentDate || !docketDate) return "";

  const payment = moment(paymentDate);
  const docket = moment(docketDate);

  if (payment.isSame(docket, "day")) {
    return payment.format("hh:mm:ss a");
  }

  return payment.format("DD-MM hh:mm a");
}

// Fetch the docket payment details for the current docket
const getDocketPayments = async () => {
  try {
    const paymentRecords = await pb.collection("docketPayments").getFullList({
      filter: `docket="${props?.docketId}"`,
      expand: "user",
    });

    // filter docket payments with 'pending' or 'waiting' status
    docketPayments.value = paymentRecords.filter((record) => {
      return !["Waiting", "Pending"].includes(record.status);
    });

    if (paymentRecords.length > 0) {
      // Calculate creditCardSurcharge
      creditCardSurcharge.value = paymentRecords.reduce(
        (accumulator: number, currentValue: any) => {
          if (currentValue.status === "Approved") {
            return accumulator + (currentValue.surchargeAmount ?? 0);
          }
          return accumulator;
        },
        0,
      );

      // Calculate creditCardtip
      creditCardTip.value = paymentRecords.reduce(
        (accumulator: number, currentValue: any) => {
          if (currentValue.status === "Approved") {
            return accumulator + (currentValue.tipAmount ?? 0);
          }
          return accumulator;
        },
        0,
      );

      // Calculate paymentsTotal
      paymentsTotal.value = paymentRecords.reduce(
        (accumulator, currentValue) => {
          if (currentValue.status === "Approved") {
            return (
              accumulator +
              (currentValue.amount ?? 0) +
              (currentValue.surchargeAmount ?? 0) +
              (currentValue.tipAmount ?? 0)
            );
          }
          return accumulator;
        },
        0,
      );
    }
  } catch (error) {}
};

// Fetch the docket line details for the current docket
const getDocketLines = async () => {
  try {
    const lineRecords = await pb.collection("docketLines").getFullList({
      filter: `docket="${props?.docketId}"`,
      expand: "userAccountCredit,product,docket,booking,giftCard,coupon",
    });

    if (lineRecords.length > 0) {
      docketLines.value = lineRecords;

      let totalPrice = 0;
      let lineTotalPrice = 0;
      for (let i = 0; i < lineRecords?.length; i++) {
        const { lineTotal, itemPrice, discount, qty } = lineRecords[i];
        totalPrice = totalPrice + qty * itemPrice;
        lineTotalPrice += lineTotal;
      }

      docketSubtotal.value = Round2Num(totalPrice, 2);

      docketTotalDiscount.value = Round2Num(totalPrice - lineTotalPrice, 2);

      // Calculate docketTotal with two decimal places as a number
      docketTotal.value = parseFloat(
        (
          docketSubtotal.value -
          docketTotalDiscount.value +
          (creditCardSurcharge.value ?? 0) +
          (creditCardTip.value ?? 0)
        ).toFixed(2),
      );

      // Calculate amountDue with two decimal places
      amountDue.value = parseFloat(
        (docketTotal.value - (paymentsTotal.value ?? 0)).toFixed(2),
      );

      // Calculate totalPaid with two decimal places
      totalPaid.value = parseFloat(
        (docketTotal.value - amountDue.value).toFixed(2),
      );
    }
  } catch (error) {}
};

// Method to get the docketline type
const getDocketLineType = (item: DocketLineDetails) => {
  if (item.transactionType == "Booking Payment") {
    return (
      `${activitiesMap.value.get(item.expand?.booking?.activity)} Booking` || ""
    );
  }
  if (item.transactionType == "Coupon") {
    const couponLabel = item?.couponQty > 1 ? "Coupons" : "Coupon";
    return `${item?.couponQty} ${couponLabel} - ${item.coupon}` || "";
  }
  if (item.transactionType == "UserCredit") {
    return "User Credit";
  }
  if (item.transactionType == "Membership Fee") {
    return "Membership Fee";
  }
  return item.expand?.product?.name || "";
};

// Method to print docket preview page
// const printDocket = () => {
//   print({
//     printable: "docket-print",
//     type: "html",
//   });
// };

const handleReceiptClick = () => {
  try {
    isReceiptDialogOpen.value = true;
    openedDocketId.value = props?.docketId;
  } catch (e) {}
};

// Lifecycle Hooks
onMounted(async () => {
  await getDocketDetails();
  await getActivitiesMap();
});
</script>

<template>
  <div>
    <div class="text-right my-4">
      <!-- <VBtn @click="printDocket">Print Document</VBtn> -->
      <VBtn @click="handleReceiptClick">Receipt</VBtn>
    </div>
    <div id="docket-print">
      <VCard elevation="0">
        <!-- docket preview header  -->
        <VContainer>
          <VRow class="justify-space-between flex-wrap align-center">
            <!-- app logo and company details  -->
            <VCol>
              <div>
                <img
                  v-if="getHeaderLogo"
                  :src="getHeaderLogo"
                  style="width: 200px; height: 147px"
                  width="400"
                  height="294"
                  alt="BayBooker"
                  class="center logoImage"
                />
              </div>
            </VCol>
            <!-- docket number and creation date  -->
            <VCol class="text-right">
              <div>
                <VCardSubtitle class="pa-0 text-h6">{{
                    globalSetup.companyName
                  }}</VCardSubtitle>
                <!-- company address  -->
                <VCardText class="pa-0"
                >{{ docketLocationDetails.address }},
                  {{ docketLocationDetails.suburb }}</VCardText
                >
                <VCardText class="pa-0"
                >{{ docketLocationDetails.city }},
                  {{ docketLocationDetails.postcode }}</VCardText
                >
                <VCardText class="pa-0"
                >{{ docketLocationDetails.email }},
                  {{ docketLocationDetails.phone }}</VCardText
                >
                <VCardText class="pa-0"
                >{{ docketLocationDetails.taxName }} #
                  {{ docketLocationDetails.taxID }}</VCardText
                >
              </div>
            </VCol>
          </VRow>
        </VContainer>
        <VDivider />
        <VContainer>
          <!-- user Details-->
          <VRow>
            <VCol class="text-left">
              <div
                v-if="
                  docketDetails?.expand?.user ||
                  docketLines[0]?.userAccountCredit ||
                  docketPayments[0]?.user
                "
              >
                <VCardSubtitle class="pa-0 text-body">
                  {{
                    `${getUserDetails.firstName} ${getUserDetails.lastName}`
                  }}</VCardSubtitle
                >
                <VCardText class="pa-0"
                >{{ getUserDetails.address }},
                  {{ getUserDetails.suburb }}</VCardText
                >
                <VCardText class="pa-0"
                >{{ getUserDetails.city }},
                  {{ getUserDetails.postcode }}</VCardText>
              </div>
              <div v-else>
                <VCardTitle>Cash Sale</VCardTitle>
              </div>
            </VCol>
            <VCol class="text-right">
              <VCardTitle>
                <VBtn v-if="docketStatus != 'Paid' && docketStatus != 'Closed'" link :href="'/pos?did=' + props?.docketId"
                      variant="plain" class="pa-0">Docket #{{ docketDetails.docketNumber }}</VBtn>
                <span v-else>Docket #{{ docketDetails.docketNumber }}</span>
              </VCardTitle>
              <div>
                <VCardText class="pb-0 pt-1" >
                  Date Created: {{ docketDetails.created }}</VCardText>
              </div>
            </VCol>
          </VRow>
        </VContainer>
        <!-- docketLine items table  -->
        <VContainer>
          <VTable hover class="text-no-wrap border rounded">
            <thead>
            <tr>
              <th>Qty</th>
              <th>Description</th>
              <th class="text-right">Item Price</th>
              <th
                v-if="
                    docketLines.length &&
                    docketLines.some((line) => line?.discount)
                  "
                class="text-right"
              >
                Discount
              </th>
              <th class="text-right">Line Total</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in docketLines" :key="item.id">
              <!-- Quantity -->
              <td>
                {{ item.qty }}
              </td>

              <!-- For Gift Cards -->
              <td v-if="item.transactionType === 'Gift Card'">
                <div style="white-space: pre-line">
                  Gift Card -
                  <template v-for="(giftCardId, index) in item.giftCard">
                    {{ giftCardId
                    }}<span v-if="index < item.giftCard.length - 1">,</span
                  ><br />
                  </template>
                </div>
              </td>

              <!-- For Event Tickets -->
              <td v-else-if="item.transactionType === 'Event'">
                <div style="white-space: pre-line">
                  {{ item.expand?.product?.name || "Event Ticket" }} -
                  <template v-for="(eventTicket, index) in item.eventTicket">
                    {{ eventTicket
                    }}<span v-if="index < item.eventTicket.length - 1">,</span
                  ><br />
                  </template>
                </div>
              </td>

              <!-- For other transaction types -->
              <td v-else-if="item.transactionType === 'UserCredit'">Account Credit</td>
              <td v-else>
                <VBtn v-if="item?.booking && item.booking != ''" class="pa-0" variant="plain"
                      :loading="bookingLoading"
                      @click="openPopup(item.booking)">{{ getDocketLineType(item) }}</VBtn>
                <span v-else>{{ getDocketLineType(item) }}</span>
                <span v-if="item?.lineText && item?.lineText !=''" class="ms-1">- {{item.lineText}}</span>

              </td>

              <!-- Price -->
              <td class="text-right">{{ formatPrice(item.itemPrice) }}</td>

              <!-- Discount -->
              <td
                v-if="
                    docketLines.length &&
                    docketLines.some((line) => line?.discount)
                  "
                class="text-right"
              >
                <div>
                  {{ item?.discount ? `${item?.discount}%` : "" }}
                </div>
              </td>

              <!-- lineTotal  -->
              <td class="text-right">{{ formatPrice(item.lineTotal) }}</td>
            </tr>
            </tbody>
          </VTable>
        </VContainer>
        <!-- docketLines details  -->
        <VContainer>
          <VRow>
            <VCol lg="6" md="6" sm="6" xs="12" cols="12" class="d-flex">
              <VRow>
                <VCol
                  v-if="getDiscountSource"
                  cols="12"
                  class="d-flex align-center flex-wrap"
                >
                  <div>{{ getDiscountSource }}</div>
                </VCol>
                <VCol class="d-flex">
                  <p class="align-self-end">
                    Total Includes {{ docketLocationDetails.taxName }} of:

                    <span>{{
                        formatPrice(
                          GSTFromTotal(
                            Number(docketTotal),
                            docketLocationDetails.taxRate ?? 0,
                          ),
                        )
                      }}</span>
                  </p>
                </VCol>
              </VRow>
            </VCol>

            <VCol lg="6" md="6" sm="6" xs="12" cols="12">
              <VRow>
                <VCol class="text-right pe-6">
                  <!-- Subtotal -->
                  <div
                    v-if="
                      (docketTotalDiscount ?? 0) ||
                      (creditCardSurcharge ?? 0) ||
                      (creditCardTip ?? 0)
                    "
                  >
                    <p>
                      Subtotal:
                      <span class="ps-4 font-weight-medium">{{
                          formatPrice(docketSubtotal)
                        }}</span>
                    </p>
                  </div>
                  <!-- Total Discount -->
                  <div v-if="docketTotalDiscount">
                    <p>
                      Total Discount:
                      <span class="ps-4 font-weight-medium">{{
                          formatPrice(docketTotalDiscount)
                        }}</span>
                    </p>
                  </div>
                  <!--Credit Card Surcharge -->
                  <div v-if="creditCardSurcharge">
                    <p>
                      Credit Card Surcharge:
                      <span class="ps-4 font-weight-medium">{{
                          formatPrice(creditCardSurcharge)
                        }}</span>
                    </p>
                  </div>
                  <!--Credit Card Tip -->
                  <div v-if="creditCardTip">
                    <p>
                      Tips:
                      <span class="ps-4 font-weight-medium">{{
                          formatPrice(creditCardTip)
                        }}</span>
                    </p>
                  </div>
                  <!-- Total -->
                  <p class="text-h6">
                    Total:
                    <span class="ps-4 font-weight-medium">{{
                        formatPrice(docketTotal)
                      }}</span>
                  </p>
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </VContainer>
        <!-- docket payments table  -->
        <VContainer>
          <VTable hover class="text-no-wrap border rounded">
            <thead>
            <tr>
              <th>Source</th>
              <th>Payment Type</th>
              <th>Date/Time</th>
              <th>Status</th>
              <th v-if="hasCardFailures">Card Failure</th>

              <th class="text-right">Amount</th>
              <th
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.surchargeAmount)
                  "
                class="text-right"
              >
                Surcharge
              </th>
              <th
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.tipAmount)
                  "
                class="text-right"
              >
                Tip
              </th>
              <th class="text-right">Total</th>
            </tr>
            </thead>
            <tbody v-if="docketPayments.length">
            <tr v-for="item in docketPayments" :key="item.id">
              <td>
                {{ item.source }}
              </td>
              <td>
                {{ item.paymentType }}
              </td>
              <td>
                {{ formatPaymentDate(item.created, docketDetails.created) }}
              </td>
              <td>
                {{ item.status }}
              </td>
              <td v-if="hasCardFailures">{{ item.cardFailureCode || "" }}</td>

              <td class="text-right">
                {{ formatPrice(item.amount) }}
              </td>
              <td
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.surchargeAmount)
                  "
                class="text-right"
              >
                <div v-if="item.surchargeAmount">
                  {{ formatPrice(item.surchargeAmount) }}
                </div>
                <div v-else></div>
              </td>

              <td
                v-if="
                    docketPayments.length &&
                    docketPayments.some((payment) => payment?.tipAmount)
                  "
                class="text-right"
              >
                <div v-if="item.tipAmount">
                  {{ formatPrice(item.tipAmount) }}
                </div>
                <div v-else></div>
              </td>
              <td class="text-right">
                  <span v-if="item.status === 'Approved'">
                    {{
                      formatPrice(
                        item.surchargeAmount + item.tipAmount + item.amount,
                      )
                    }}
                  </span>
                <span v-else> $0.00 </span>
              </td>
            </tr>
            </tbody>
          </VTable>
        </VContainer>
        <!-- docket amount due  -->
        <VContainer>
          <VRow class="align-center">
            <VCol>
              <div>
                <VNodeRenderer :nodes="themeConfig.app.logo" />
              </div>
            </VCol>

            <VCol class="text-right pe-6">
              <p class="text-h6">
                Total Paid:
                <span class="ps-4">{{ formatPrice(totalPaid) }}</span>
              </p>
              <p class="text-h6">
                Amount Due:
                <span class="ps-4">{{ formatPrice(amountDue) }}</span>
              </p>
            </VCol>
          </VRow>
        </VContainer>
      </VCard>
    </div>
    <ReceiptDialog
      :is-visible="isReceiptDialogOpen"
      :docket-id="openedDocketId"
      @close-dialog="
        () => {
          (isReceiptDialogOpen = false), (openedDocketId = '');
        }
      "
    />
  </div>
</template>

<style lang="scss" scoped>
p {
  margin-block-end: 8px;
}

.logoImage {
  block-size: 294px;
  inline-size: 400px;
  object-fit: contain;

  @media (max-width: 600px) {
    block-size: auto;
    inline-size: 100%;
  }
}
</style>
